import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Download, Calendar } from 'lucide-react';
import { feeCollectionAPI, monthlyAnalysisAPI, formatCurrency, formatNumber } from '../services/api';
import PDFExporter from '../utils/pdfExporter';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const ReportSummary = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({});
  const [ytd, setYtd] = useState({});
  const [month, setMonth] = useState(DEFAULT_REPORT_MONTH);
  const [year, setYear] = useState(DEFAULT_REPORT_YEAR);
  const [isExporting, setIsExporting] = useState(false);

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when report period changes
  }, [month, year]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const periodParams = { month, year };
      const [currentRes, ytdRes, overviewRes] = await Promise.all([
        monthlyAnalysisAPI.getCurrentSummary(periodParams),
        monthlyAnalysisAPI.getYtdPerformance(periodParams),
        feeCollectionAPI.getOverview(periodParams),
      ]);

      const current = currentRes?.data || {};
      const overview = overviewRes?.data?.overview || overviewRes?.data?.summary || {};
      setSummary({
        totalSubscribers: Number(current.total_subscribers ?? current.totalSubscribers ?? 0) || 0,
        activeSubscribers: Number(current.active_subscribers ?? current.activeSubscribers ?? 0) || 0,
        totalCollections: Number(
          overview.totalCollected ?? overview.collected_amount ?? current.total_collected ?? 0
        ) || 0,
        complianceRate: Number(
          current.collection_rate ?? overview.collectionRate ?? overview.collection_rate ?? 0
        ) || 0,
      });

      const ytdRaw = ytdRes?.data || {};
      setYtd({
        ytdRevenue: Number(ytdRaw.total_revenue_ytd ?? ytdRaw.ytdRevenue ?? 0) || 0,
        ytdSubscribersGrowth: Number(ytdRaw.net_subscriber_growth ?? ytdRaw.ytdSubscribersGrowth ?? 0) || 0,
        ytdComplianceRate: Number(ytdRaw.avg_collection_rate ?? ytdRaw.ytdComplianceRate ?? 0) || 0,
      });
    } catch (err) {
      console.error(err);
      setError(err.message || t('reportSummary.loadError'));
      setSummary({});
      setYtd({});
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    const monthName = months.find(m => m.value === month)?.label || 'Current';
    const filename = `summary-report-${monthName.toLowerCase()}`;
    const result = await PDFExporter.exportToPDF('report-summary-standard', filename, { scale: 1.5 });
    if (!result.success) alert(t('common.exportPdfFailed') + ': ' + result.error);
    setIsExporting(false);
  };

  return (
    <div id="report-summary-standard" className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="h-8 w-8 mr-3 text-blue-600" />
            {t('reportSummary.title')}
          </h1>
          <p className="text-gray-600 mt-2">{t('reportSummary.subtitle')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500">
              {months.map(m => <option key={m.value} value={m.value}>{t(`common.monthNames.${m.value}`)}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500">
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button onClick={handleExportPDF} disabled={isExporting} className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm ${isExporting ? 'bg-gray-400 text-gray-200' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
            <Download className="h-4 w-4" />
            <span>{isExporting ? t('common.exporting') : t('common.exportPDF')}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg shadow">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4" />
          <p className="text-gray-600">{t('reportSummary.loading')}</p>
        </div>
      ) : (
      <>
      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">{t('reportSummary.totalSubscribers')}</div>
          <div className="text-2xl font-bold">{formatNumber(summary.totalSubscribers || 0)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">{t('reportSummary.activeSubscribers')}</div>
          <div className="text-2xl font-bold text-green-700">{formatNumber(summary.activeSubscribers || 0)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">{t('reportSummary.totalCollections')}</div>
          <div className="text-2xl font-bold text-green-700">{formatCurrency(summary.totalCollections || 0)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">{t('reportSummary.complianceRate')}</div>
          <div className="text-2xl font-bold">{Number.isFinite(Number(summary.complianceRate)) ? Number(summary.complianceRate).toFixed(1) : '0.0'}%</div>
        </div>
      </div>

      {/* YTD section */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold">{t('reportSummary.ytdTitle')}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
          <div>
            <div className="text-sm text-gray-600">{t('reportSummary.ytdRevenue')}</div>
            <div className="text-xl font-bold">{Number(ytd.ytdRevenue || 0).toFixed(1)}B ₭</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">{t('reportSummary.ytdSubscribersGrowth')}</div>
            <div className="text-xl font-bold">{formatNumber(ytd.ytdSubscribersGrowth || 0)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">{t('reportSummary.ytdAvgCompliance')}</div>
            <div className="text-xl font-bold">{Number.isFinite(Number(ytd.ytdComplianceRate)) ? Number(ytd.ytdComplianceRate).toFixed(1) : '0.0'}%</div>
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-500">{t('reportSummary.footer')}</div>
      </>
      )}
    </div>
  );
};

export default ReportSummary;
